import { useState, useRef, useEffect } from 'react';
import { Card, Form, Select, Input, Upload, Button, Tooltip, message } from 'antd';
import { QuestionCircleOutlined, CloseOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import feedbackService from '../../services/feedback.service';
import { eventBus, EVENTS } from '../../utils/eventBus';
import './FeedbackPopup.css';

const { TextArea } = Input;
const { Option } = Select;

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Báo lỗi' },
  { value: 'improvement', label: 'Góp ý cải tiến' },
  { value: 'review', label: 'Đánh giá/Nhận xét' },
  { value: 'other', label: 'Khác' },
];

const PLACEHOLDERS = {
  bug: 'Bạn gặp lỗi ở đâu? Thao tác nào dẫn tới lỗi?',
  improvement: 'Bạn mong muốn sản phẩm cải thiện điều gì?',
  review: 'Điều bạn thích hoặc chưa hài lòng?',
  other: 'Mô tả chi tiết phản hồi của bạn...',
};

const MAX_CHARS = 1000;

const FeedbackPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [feedbackType, setFeedbackType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const popupRef = useRef(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        // Check if click is on the floating button
        const floatingButton = document.querySelector('.feedback-floating-button');
        if (floatingButton && floatingButton.contains(event.target)) {
          return;
        }
        // Check if click is on Ant Design dropdown overlay (rendered outside popup in portal)
        const antDropdown = document.querySelector('.ant-select-dropdown');
        if (antDropdown && antDropdown.contains(event.target)) {
          return;
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('type', values.type);
      formData.append('description', values.description);
      if (values.email) {
        formData.append('email', values.email);
      }
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('attachment', fileList[0].originFileObj);
      }

      await feedbackService.submit(formData);
      message.success('Cảm ơn bạn! Phản hồi đã được ghi nhận');
      handleClose();
    } catch {
      message.error('Có lỗi xảy ra, vui lòng thử lại');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    form.resetFields();
    setCharCount(0);
    setFeedbackType(null);
    setFileList([]);
  };

  const handleDescriptionChange = (e) => {
    setCharCount(e.target.value.length);
  };

  // Lắng nghe event từ BetaWelcomeModal hoặc các nơi khác muốn mở feedback
  useEffect(() => {
    const unsubscribe = eventBus.on(EVENTS.OPEN_FEEDBACK_POPUP, () => {
      setIsOpen(true);
    });
    return unsubscribe;
  }, []);

  const handleTypeChange = (value) => {
    setFeedbackType(value);
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(-1)); // Only keep the last file
  };

  const getCharCountClass = () => {
    if (charCount > MAX_CHARS) return 'error';
    if (charCount > MAX_CHARS * 0.9) return 'warning';
    return '';
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Tải ảnh</div>
    </div>
  );

  return (
    <>
      {/* Floating Button */}
      <button
        className="feedback-floating-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Gửi phản hồi"
      >
        <QuestionCircleOutlined />
      </button>

      {/* Popup */}
      {isOpen && (
        <div className="feedback-popup-overlay" ref={popupRef}>
          <Card className="feedback-popup-card">
            <Button
              type="text"
              icon={<CloseOutlined />}
              className="feedback-close-btn"
              onClick={handleClose}
            />

            <div className="feedback-popup-header">
              <h3>Gửi phản hồi cho chúng tôi</h3>
              <p>Ý kiến của bạn giúp chúng tôi cải thiện sản phẩm tốt hơn.</p>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="feedback-form"
            >
              <Form.Item
                name="type"
                label="Phân loại phản hồi"
                rules={[{ required: true, message: 'Vui lòng chọn loại phản hồi' }]}
              >
                <Select
                  placeholder="Chọn loại phản hồi"
                  onChange={handleTypeChange}
                >
                  {FEEDBACK_TYPES.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="description"
                label="Mô tả chi tiết"
                rules={[
                  { required: true, message: 'Vui lòng nhập mô tả' },
                  { max: MAX_CHARS, message: `Tối đa ${MAX_CHARS} ký tự` },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder={PLACEHOLDERS[feedbackType] || 'Nhập mô tả chi tiết...'}
                  onChange={handleDescriptionChange}
                  maxLength={MAX_CHARS + 100} // Allow typing slightly over for UX
                />
              </Form.Item>
              <div className={`feedback-char-count ${getCharCountClass()}`}>
                {charCount}/{MAX_CHARS}
              </div>

              <Form.Item
                name="attachment"
                label="Đính kèm ảnh (tùy chọn)"
              >
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  onChange={handleUploadChange}
                  beforeUpload={() => false} // Prevent auto upload
                  accept="image/*"
                  className="feedback-upload"
                >
                  {fileList.length < 1 && uploadButton}
                </Upload>
              </Form.Item>

              <Form.Item
                name="email"
                label={
                  <span className="feedback-email-label">
                    Email (tùy chọn)
                    <Tooltip mouseEnterDelay={1.5} title="Nếu bạn muốn chúng tôi phản hồi lại">
                      <InfoCircleOutlined className="feedback-email-tooltip" />
                    </Tooltip>
                  </span>
                }
                rules={[
                  { type: 'email', message: 'Email không hợp lệ' },
                ]}
              >
                <Input placeholder="example@email.com" />
              </Form.Item>

              <div className="feedback-buttons">
                <Button
                  className="feedback-cancel-btn"
                  onClick={handleClose}
                >
                  Hủy
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="feedback-submit-btn"
                >
                  Gửi phản hồi
                </Button>
              </div>
            </Form>
          </Card>
        </div>
      )}
    </>
  );
};

export default FeedbackPopup;
